// ═══════════════════════════════════════════════════════════════
// RollCall — Check-In
// Select an event, pick a person from the directory, check in or out.
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, Select, Stack,
} from '../../components/gab/GabComponents';
import { IconLogin, IconLogout, IconClock, IconMapPin } from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useAuthStore } from '../../store/authStore';
import { useRollCallStore } from '../../store/rollcall-store';
import { PeoplePicker, type PickedPerson } from '../../components/ui/PeoplePicker';
import { primary, semantic } from '../../lib/colors';

export default function CheckIn() {
  const { user, incidents } = useAuthStore();
  const { events, checkins, fetchEvents, fetchCheckins, checkIn, checkOut } = useRollCallStore();

  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [personId, setPersonId] = useState<string | null>(null);
  const [person, setPerson] = useState<PickedPerson | null>(null);
  const [processing, setProcessing] = useState(false);
  const [lastAction, setLastAction] = useState<{
    type: 'checkin' | 'checkout'; name: string; agency: string; time: Date;
  } | null>(null);
  const [geoLocation, setGeoLocation] = useState<{ lat: number; lng: number } | null>(null);

  useEffect(() => {
    fetchEvents();
    fetchCheckins();
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => setGeoLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => {}
      );
    }
  }, []);

  const openEvents = events.filter(e => e.eventStatus === 'open');
  const selectedEvent = openEvents.find(e => String(e.id) === selectedEventId);
  const incidentName = selectedEvent
    ? incidents.find(i => String(i.id) === String(selectedEvent.incidentId))?.name || ''
    : '';

  // Is this person currently checked in to the selected event?
  const activeCheckin = person && selectedEventId
    ? checkins.find(c =>
        String(c.eventId) === selectedEventId &&
        (String(c.personId) === String(person.record_id) || c.personName === person.fullName) &&
        !c.checkOutTime
      )
    : null;

  const handleAction = async () => {
    if (!person || !selectedEventId) return;
    setProcessing(true);
    try {
      if (activeCheckin) {
        await checkOut(activeCheckin.id);
        setLastAction({ type: 'checkout', name: person.fullName, agency: person.department, time: new Date() });
        notifications.show({ color: 'var(--color-primary-700)', title: 'Checked Out', message: `${person.fullName} checked out` });
      } else {
        await checkIn({
          eventId: Number(selectedEventId),
          personId: String(person.record_id),
          personName: person.fullName,
          agency: person.department || '',
          method: 'admin',
          ...(geoLocation ? { geoLat: String(geoLocation.lat), geoLng: String(geoLocation.lng) } : {}),
        });
        setLastAction({ type: 'checkin', name: person.fullName, agency: person.department, time: new Date() });
        notifications.show({ color: 'var(--color-success)', title: 'Checked In', message: `${person.fullName} checked in` });
      }
      await fetchCheckins();
    } catch (err: any) {
      notifications.show({ message: err?.message || 'Action failed', color: 'var(--color-error)' });
    } finally {
      setProcessing(false);
    }
  };

  const accentColor = activeCheckin ? primary[700] : semantic.success;

  return (
    <div style={{ maxWidth: 520, margin: '0 auto' }}>
      <Title order={3} mb="md" c="black">Check-In</Title>

      <Stack gap="md">
        {/* Event selector */}
        <Select
          label="Event"
          placeholder="Select an open event…"
          data={openEvents.map(e => ({ value: String(e.id), label: e.eventName }))}
          value={selectedEventId}
          onChange={v => { setSelectedEventId(v); setPersonId(null); setPerson(null); }}
          searchable
        />

        {selectedEvent && (
          <Paper p="sm" withBorder radius="sm" style={{ background: 'var(--color-neutral-50)' }}>
            <Text size="sm" fw={600}>{selectedEvent.eventName}</Text>
            {incidentName && <Text size="xs" c="dimmed">{incidentName}</Text>}
            {geoLocation && (
              <Group gap={4} mt={4}>
                <IconMapPin size={11} color={semantic.success} />
                <Text size="xs" c="dimmed">GPS acquired</Text>
              </Group>
            )}
          </Paper>
        )}

        {/* Person picker */}
        {selectedEventId && (
          <PeoplePicker
            label="Person"
            placeholder="Search employees and liaisons…"
            typeFilter="employee,liaison"
            value={personId}
            onChange={(id, p) => { setPersonId(id); setPerson(p); }}
          />
        )}

        {/* Status + action */}
        {person && selectedEventId && (
          <Paper p="md" withBorder radius="sm" style={{ borderLeft: `3px solid ${accentColor}` }}>
            <Group justify="space-between" align="center">
              <div>
                <Text fw={600}>{person.fullName}</Text>
                <Text size="xs" c="dimmed">
                  {[person.title, person.department].filter(Boolean).join(' · ') || person.type}
                </Text>
                {activeCheckin && (
                  <Group gap={4} mt={4}>
                    <IconClock size={11} />
                    <Text size="xs" c="dimmed">
                      Checked in at {new Date(activeCheckin.checkInTime).toLocaleTimeString()}
                    </Text>
                  </Group>
                )}
              </div>
              <Badge variant="light" color={activeCheckin ? 'var(--color-primary-700)' : '#939598'} size="sm">
                {activeCheckin ? 'IN' : 'NOT IN'}
              </Badge>
            </Group>
            <Button
              fullWidth
              mt="sm"
              color={activeCheckin ? 'var(--color-primary-700)' : 'var(--color-success)'}
              leftSection={activeCheckin ? <IconLogout size={14} /> : <IconLogin size={14} />}
              onClick={handleAction}
              loading={processing}
            >
              {activeCheckin ? `Check Out ${person.firstName}` : `Check In ${person.firstName}`}
            </Button>
          </Paper>
        )}

        {/* Last action confirmation */}
        {lastAction && (
          <Paper p="sm" withBorder radius="sm" style={{
            borderLeft: `3px solid ${lastAction.type === 'checkin' ? semantic.success : primary[700]}`,
          }}>
            <Group gap="xs">
              {lastAction.type === 'checkin'
                ? <IconLogin size={14} color={semantic.success} />
                : <IconLogout size={14} color={primary[700]} />}
              <Text size="sm" fw={600}>{lastAction.name}</Text>
              <Text size="xs" c="dimmed">· {lastAction.time.toLocaleTimeString()}</Text>
            </Group>
            {lastAction.agency && <Text size="xs" c="dimmed" ml={22}>{lastAction.agency}</Text>}
          </Paper>
        )}
      </Stack>
    </div>
  );
}
